import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo } from "react";
import { useStore } from "store";
import MenuItem from "./MenuItem";
import MenuFilter from "./MenuFilter";
import Cart from "./Cart";
import { useQuery } from "react-query";
import api from "api";
import ReactSelect from "react-select";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { queryKeys } from "queries";
import useQueryUser from "queries/user";

const GuestOrUserOption = observer(() => {
	const { cart } = useStore();
	return (
		<div className="flex divide-x divide-primary border border-primary rounded mr-1">
			<button className={`py-1.5 px-1.5 ${cart.isGuestOrder ? "bg-primary text-white" : ""}`} onClick={() => cart.setIsGuestOrder(true)}>
				Guest
			</button>
			<button className={`py-1.5 px-1.5 ${cart.isGuestOrder ? "" : "bg-primary text-white"}`} onClick={() => cart.setIsGuestOrder(false)}>
				Member
			</button>
		</div>
	);
});

const UserSelectDropdown: FC = observer(() => {
	const { data: users, isLoading } = useQueryUser({ filterCallback: (users) => users.filter((user) => user) });
	const userOptions = useMemo(
		() =>
			users
				?.sort((a, b) => a.firstName.localeCompare(b.firstName))
				.map((user) => ({
					value: user,
					label: user.firstName + " " + user.lastName + " " + " [" + Number(user.membershipPoints).toFixed(2) + " ]",
				})) || [],
		[users]
	);
	const { cart } = useStore();

	return (
		<div className="flex items-center relative">
			<GuestOrUserOption />
			<ReactSelect
				options={userOptions}
				isLoading={isLoading}
				isDisabled={cart.isGuestOrder}
				value={cart.orderingUser ? userOptions.find((user) => user.value.id === cart.orderingUser?.id) : null}
				menuPlacement="top"
				className="text-black rounded w-56 disabled:cursor-not-allowed"
				onChange={(selectedUser) => selectedUser?.value && cart.setOrderingUser(selectedUser.value)}
			/>
		</div>
	);
});

const DiscountRateLabel: FC = observer(() => {
	const { cart } = useStore();
	return (
		<div className="flex">
			<div className="bg-primary border border-primary px-2 py-1.5 rounded rounded-r-none">Multiplier</div>
			<div className="border border-primary px-2 py-1.5 rounded rounded-l-none">{(cart.priceMultiplier * 100).toFixed(2)}%</div>
		</div>
	);
});

const MenuItems: FC = observer(() => {
	const { menu } = useStore();

	useEffect(() => {
		menu.getItems();
	}, []);
	return (
		<div className="grid grid-rows-6 lg:grid-cols-3 xl:grid-cols-4 grid-cols-2 gap-2 max-h-[calc(100vh-144px)] overflow-y-auto pr-2 pl-4">
			{menu.items.map((item) => (
				<MenuItem item={item} key={item.id} />
			))}
		</div>
	);
});

const MenuPagination: FC = observer(() => {
	const { menu } = useStore();
	return (
		<div className="border border-primary rounded flex divide-x divide-primary items-center">
			<button className="text-primary py-1.5 px-2" onClick={() => menu.setPage((prev) => prev - 1)}>
				<FaChevronLeft />
			</button>
			<button className="text-primary py-1.5 px-2 font-bold" onClick={() => menu.setPage(0)}>
				First
			</button>
			<span className="text-primary py-1.5 px-2 font-bold">{menu.page + 1}</span>
			<button className="text-primary py-1.5 px-2 font-bold" onClick={menu.setLastPage}>
				Last
			</button>
			<button className="text-primary py-1.5 px-2" onClick={() => menu.setPage((prev) => prev + 1)}>
				<FaChevronRight />
			</button>
		</div>
	);
});

const Menu: FC = () => {
	return (
		<div className="h-full flex gap-x-5 pr-5">
			<div className="flex-1 flex flex-col">
				<div className="flex-1 flex divide-x-2 divide-gray-primary">
					<MenuFilter />
					<MenuItems />
				</div>
				<div className="flex justify-between items-center ml-5 mr-2 space-x-1">
					<div className="flex justify-between items-center space-x-1">
						<UserSelectDropdown />
						<DiscountRateLabel />
					</div>
					<MenuPagination />
				</div>
			</div>
			<div className="w-96 h-[calc(100vh-84px)] flex flex-col">
				<Cart />
			</div>
		</div>
	);
};

export default Menu;
